<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, type ComputedRef, type Ref } from "vue";
import { useRoute, useRouter, type RouteLocationNormalizedLoaded, type Router } from "vue-router";
import type { TenantSummary } from "../api/tenants.js";
import { listTiers, type ServiceTier } from "../api/system-tiers.js";
import { canManageTenants } from "../api/system-authorization.js";
import AppShell from "../components/AppShell.vue";
import TenantCreateForm from "../components/TenantCreateForm.vue";
import { useAuthenticationStore } from "../stores/authentication.js";

const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const router: Router = useRouter();
const route: RouteLocationNormalizedLoaded = useRoute();
const tiers: Ref<ServiceTier[]> = ref([]);
const canCreateTenant: ComputedRef<boolean> = computed(
  (): boolean => canManageTenants(authenticationStore.session?.user.systemRoles ?? []),
);
const activeTiers: ComputedRef<readonly ServiceTier[]> = computed(
  (): readonly ServiceTier[] => tiers.value.filter((tier: ServiceTier): boolean => tier.status === "active"),
);
const tenantServiceUnavailable: ComputedRef<boolean> = computed(
  (): boolean => route.query.unavailable === "1",
);

async function openTenant(tenant: TenantSummary): Promise<void> {
  await router.replace({ name: "tenant-overview", params: { tenantId: tenant.id } });
}

async function loadTiers(): Promise<void> {
  if (!canCreateTenant.value) {
    return;
  }
  tiers.value = [...await listTiers()];
}

onMounted((): void => {
  void loadTiers();
});
</script>

<template>
  <AppShell>
    <div class="page-content--narrow">
      <section class="content-panel empty-tenant-panel">
        <p class="eyebrow">Tenant access</p>
        <h1>{{ tenantServiceUnavailable ? "Tenant service unavailable" : "No tenant available" }}</h1>
        <p v-if="tenantServiceUnavailable">
          Your login session is active, but the tenant list could not be loaded. Check the server logs
          and reload this page to try again.
        </p>
        <p v-else-if="!canCreateTenant">
          This account does not currently belong to an active tenant. Ask a tenant administrator to
          add your email address.
        </p>
        <template v-else-if="!tenantServiceUnavailable">
          <template v-if="activeTiers.length === 0">
            <p>Create an active tier before creating the first tenant.</p>
            <RouterLink class="button button--primary" :to="{ name: 'system-tiers' }">Open tier configuration</RouterLink>
          </template>
          <template v-else>
            <p>Create the first tenant for this LacunaScope installation.</p>
            <TenantCreateForm :tiers="activeTiers" @created="openTenant" />
          </template>
        </template>
      </section>
    </div>
  </AppShell>
</template>
